import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { Line } from "react-chartjs-2";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../../components/LoadingSpinner";
import { fetchGroups } from "../../services/groupService";
import { fetchGroupTransactions, fetchDeskOfficerNotifications } from "../../services/deskOfficerService";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const brand = "#800b33";
const brandHover = "#6c0a2b";
const MONTHS_TO_DISPLAY = 6;
const monthFormatter = new Intl.DateTimeFormat("en-TZ", { month: "short", year: "numeric" });

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.45, ease: "easeOut" },
  }),
};

const formatTimeAgo = (timestamp, t) => {
  if (!timestamp) return "";
  const created = new Date(timestamp);
  if (Number.isNaN(created.getTime())) return "";
  const diffMs = Date.now() - created.getTime();
  if (diffMs < 0) return t("just now");
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return t("{{count}} seconds ago", { count: Math.round(diffMs / 1000) });
  if (diffMs < hour) return t("{{count}} minutes ago", { count: Math.round(diffMs / minute) });
  if (diffMs < day) return t("{{count}} hours ago", { count: Math.round(diffMs / hour) });
  return t("{{count}} days ago", { count: Math.round(diffMs / day) });
};

const getMonthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const buildMonthBuckets = (count) => {
  const buckets = [];
  const today = new Date();
  for (let offset = count - 1; offset >= 0; offset -= 1) {
    const bucketDate = new Date(today.getFullYear(), today.getMonth() - offset, 1);
    buckets.push({
      key: getMonthKey(bucketDate),
      label: monthFormatter.format(bucketDate),
    });
  }
  return buckets;
};

const buildChartSeries = (transactions = [], count = MONTHS_TO_DISPLAY) => {
  const buckets = buildMonthBuckets(count);
  const contributions = buckets.reduce((acc, bucket) => ({ ...acc, [bucket.key]: 0 }), {});
  const disbursements = buckets.reduce((acc, bucket) => ({ ...acc, [bucket.key]: 0 }), {});

  transactions.forEach((tx) => {
    if (!tx?.created_at) return;
    const txDate = new Date(tx.created_at);
    if (Number.isNaN(txDate.getTime())) return;
    const key = getMonthKey(txDate);
    if (!Object.prototype.hasOwnProperty.call(contributions, key)) return;
    const amount = Number(tx.amount) || 0;
    if (tx.transaction_type === "MEMBER_CONTRIBUTION") {
      contributions[key] += amount;
    }
    if (["LOAN_DISBURSEMENT", "MEMBER_WITHDRAWAL"].includes(tx.transaction_type)) {
      disbursements[key] += Math.abs(amount);
    }
  });

  return {
    labels: buckets.map((bucket) => bucket.label),
    contributions: buckets.map((bucket) => contributions[bucket.key] || 0),
    disbursements: buckets.map((bucket) => disbursements[bucket.key] || 0),
  };
};

const normalizeListResponse = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const StatCard = ({ icon, label, value, index }) => (
  <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={index}>
    <Card
      sx={{
        p: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderRadius: 3,
        boxShadow: 3,
        "&:hover": { boxShadow: 6, transform: "translateY(-4px)" },
        transition: "all .25s ease",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            backgroundColor: `${brand}1A`,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            {typeof value === "number" ? new Intl.NumberFormat("en-TZ").format(value) : value}
          </Typography>
        </Box>
      </Box>
    </Card>
  </motion.div>
);

const DeskOfficerDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    const loadDashboard = async () => {
      setLoading(true);
      try {
        const [groupsData, transactionsPage, notificationsPage] = await Promise.all([
          fetchGroups({ signal: controller.signal }),
          fetchGroupTransactions({ signal: controller.signal }),
          fetchDeskOfficerNotifications({ signal: controller.signal }),
        ]);
        setGroups(groupsData ?? []);
        setTransactions(normalizeListResponse(transactionsPage));
        setNotifications(normalizeListResponse(notificationsPage));
        setError(null);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("DeskOfficerDashboard error:", err);
          setError(err.message || t("Unable to load dashboard data"));
        }
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
    return () => controller.abort();
  }, [t]);

  const stats = useMemo(() => {
    const totalGroups = groups.length;
    const approvedGroups = groups.filter((grp) => grp.is_active).length;
    const pendingGroups = Math.max(totalGroups - approvedGroups, 0);
    const totalMembers = groups.reduce((sum, grp) => sum + (grp.members_count || 0), 0);
    return [
      { label: t("Total Groups"), value: totalGroups, icon: <GroupsIcon sx={{ fontSize: 28, color: brand }} /> },
      { label: t("Pending Approvals"), value: pendingGroups, icon: <PendingActionsIcon sx={{ fontSize: 28, color: brand }} /> },
      { label: t("Approved Groups"), value: approvedGroups, icon: <CheckCircleIcon sx={{ fontSize: 28, color: brand }} /> },
      { label: t("Total Members"), value: totalMembers, icon: <PersonAddAltIcon sx={{ fontSize: 28, color: brand }} /> },
    ];
  }, [groups, t]);

  const recentGroups = useMemo(() => {
    const sorted = [...groups].sort((a, b) => {
      const dateB = new Date(b.created_at).getTime() || 0;
      const dateA = new Date(a.created_at).getTime() || 0;
      return dateB - dateA;
    });
    return sorted.slice(0, 5);
  }, [groups]);

  const chartSeries = useMemo(() => buildChartSeries(transactions), [transactions]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom" } },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (value) => new Intl.NumberFormat("en-TZ").format(value) },
      },
      x: { grid: { display: false } },
    },
  };

  const notificationItems = useMemo(
    () =>
      notifications.map((notification) => {
        const id = notification.id ?? notification.pk ?? `${notification.type}-${notification.created_at}`;
        return {
          id,
          text: notification.message || notification.text || "",
          time: formatTimeAgo(notification.created_at, t),
        };
      }),
    [notifications, t]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
            {t("Desk Officer Dashboard")}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {t("Register groups, manage approvals, and keep leaders informed")}
          </Typography>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <LoadingSpinner />
        </Box>
      )}

      {error && !loading && (
        <Typography color="error" sx={{ mb: 3 }}>
          {error || t("Unable to load dashboard data")}
        </Typography>
      )}

      {!loading && !error && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {stats.map((stat, idx) => (
              <Grid item xs={12} sm={6} md={3} key={stat.label}>
                <StatCard icon={stat.icon} label={stat.label} value={stat.value} index={idx} />
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1}>
                <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
                    {t("Group Registration Trend (Last 6 Months)")}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ height: 300 }}>
                    {chartSeries.labels.length > 0 ? (
                      <Line data={{
                        labels: chartSeries.labels,
                        datasets: [
                          {
                            label: t("contributions"),
                            data: chartSeries.contributions,
                            borderColor: brand,
                            backgroundColor: `${brand}24`,
                            fill: true,
                            tension: 0.35,
                            pointRadius: 3,
                          },
                          {
                            label: t("Disbursements"),
                            data: chartSeries.disbursements,
                            borderColor: "#ff8f00",
                            backgroundColor: "rgba(255,143,0,.15)",
                            fill: true,
                            tension: 0.35,
                            pointRadius: 3,
                          },
                        ],
                      }} options={chartOptions} />
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {t("No transaction data available for the selected period.")}
                      </Typography>
                    )}
                  </Box>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={4}>
              <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.2}>
                <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
                    {t("Quick Actions")}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    <Button onClick={() => navigate("/app/desk-officer/groups")} variant="contained" sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}>
                      {t("Group Management")}
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: brand,
                        color: brand,
                        "&:hover": { borderColor: brandHover, color: brandHover },
                      }}
                      onClick={() => navigate("/app/loan-approvals")}
                    >
                      {t("Review Pending Approvals")}
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: brand,
                        color: brand,
                        "&:hover": { borderColor: brandHover, color: brandHover },
                      }}
                      onClick={() => navigate("/app/desk-officer/groups")}
                    >
                      {t("Generate Registration Link")}
                    </Button>
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} md={7}>
              <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.4}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {t("Recent Group Registrations")}
                      </Typography>
                      <Button size="small" variant="contained" sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}>
                        {t("View All")}
                      </Button>
                    </Box>
                    <TableContainer component={Paper} elevation={0}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>{t("Group")}</TableCell>
                            <TableCell>{t("Frequency")}</TableCell>
                            <TableCell>{t("Chairperson")}</TableCell>
                            <TableCell>{t("Members")}</TableCell>
                            <TableCell>{t("Status")}</TableCell>
                            <TableCell>{t("Date")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {recentGroups.length > 0 ? (
                            recentGroups.map((group, idx) => (
                              <TableRow key={group.id} hover>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>{group.name}</TableCell>
                                <TableCell>{group.frequency}</TableCell>
                                <TableCell>{group.chairperson?.full_name || t("N/A")}</TableCell>
                                <TableCell>{group.members_count || 0}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={group.is_active ? t("Approved") : t("Pending")}
                                    size="small"
                                    color={group.is_active ? "success" : "warning"}
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell>{group.created_at ? new Date(group.created_at).toLocaleDateString() : "-"}</TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={7} align="center">
                                {t("No recent group registrations found.")}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={5}>
              <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.6}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: brand }}>
                      {t("layout.notificationsTitle")}
                    </Typography>
                    {notificationItems.length > 0 ? (
                      notificationItems.map((notification) => (
                        <motion.div
                          key={notification.id}
                          whileHover={{ scale: 1.02 }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "12px",
                            borderRadius: "10px",
                            background: "#f9fafb",
                            marginBottom: "10px",
                            border: "1px solid #f0f0f0",
                          }}
                        >
                          <NotificationsActiveIcon sx={{ color: brand, marginRight: 12 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {notification.text}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {notification.time}
                            </Typography>
                          </Box>
                        </motion.div>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                        {t("layout.noNotifications")}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </>
      )}

      <Typography
        variant="caption"
        sx={{ display: "block", textAlign: "center", color: "text.secondary", mt: 4 }}
      >
        {t("© {year} Savvy Community", { year: new Date().getFullYear() })}
      </Typography>
    </Box>
  );
};

export default DeskOfficerDashboard;
